/**
 * Unified Navigation Configuration
 * Single source of truth for all navigation, pages, sections, and subpages
 * Based on Riseon navigation system adapted for MQCapital
 */

import ROUTES from './routes';

const fileSignalTermsAndConditions =
  'https://www.dropbox.com/scl/fi/db8eghrna1dzzkusilivh/file-signal-terms-and-conditions.pdf?rlkey=ddex4bklfpcxz884x9xpo112k&st=pegw51yx&raw=1';
const fileFaqMqcapital =
  'https://www.dropbox.com/scl/fi/7jze9fhp0t3jn8tajofq3/file-faq-mqcapital.pdf?rlkey=41b1ujohb4teglbp15yzbtf0q&st=ntdp5kib&raw=1';
const fileSecuritizationLuxembourg =
  'https://www.dropbox.com/scl/fi/swjhv1y4zo56k6fb291ha/file-securitization.pdf?rlkey=yyo7cnmuxd6mdeobegvqrdqst&st=g6ye8vrd&raw=1';

/**
 * Site Map - Complete structure of all pages and their sections
 * Each page can have:
 * - path: Route path
 * - title: Display title in navigation
 * - component: Component name (for reference)
 * - sections: Array of sections on the page (for scroll navigation)
 * - subPages: Hidden child pages not shown in main navigation
 */
export const siteMap = {
  home: {
    path: ROUTES.HOME,
    title: 'Home',
    component: 'HomePage'
  },

  aboutUs: {
    path: ROUTES.ABOUT_US.ROOT,
    title: 'About Us',
    component: 'AboutUsPage',
    sections: [
      { id: 'mission', title: 'Mission', level: 'h2' },
      { id: 'leadership', title: 'Leadership', level: 'h2' },
      { id: 'principles', title: 'Principles', level: 'h2' }
    ],
    subPages: {
      csr: {
        path: ROUTES.ABOUT_US.CSR,
        title: 'CSR',
        component: 'CSRPage',
        sections: [],
        showInNav: false // Hide from navigation
      }
    }
  },

  strategies: {
    path: ROUTES.STRATEGIES.ROOT,
    title: 'Strategies',
    component: 'StrategiesPage',
    sections: [
      { id: 'technology', title: 'Technology', level: 'h2' },
      { id: 'programs', title: 'Programs', level: 'h2' },
      { id: 'swingvision', title: 'SwingVision', level: 'h2' },
      { id: 'accessing', title: 'Accessing Our Strategies', level: 'h2' },
      { id: 'riskManagement', title: 'Risk Management', level: 'h2' },
      {
        id: 'signal-terms',
        title: 'Terms and Conditions',
        path: fileSignalTermsAndConditions,
        isFile: true,
        fileType: 'pdf'
      },
      {
        id: 'faq',
        title: 'FAQ',
        path: fileFaqMqcapital,
        isFile: true,
        fileType: 'pdf'
      }
    ],
    // Hidden subpages - accessible via program cards only
    subPages: {
      quantitativeStrategies: {
        path: ROUTES.STRATEGIES.INVESTMENT_PROGRAMS,
        title: 'Quantitative Strategies',
        component: 'InvestmentProgramsPage',
        sections: []
      },
      tradingBusiness: {
        path: ROUTES.STRATEGIES.TRADING_BUSINESS,
        title: 'Trading Business Vision',
        component: 'TradingBusinessPage',
        sections: []
      },
      strandQ: {
        path: ROUTES.STRATEGIES.STRAND_Q,
        title: 'Strand Q',
        component: 'StrandQPage',
        sections: []
      },
      merin: {
        path: ROUTES.STRATEGIES.MERIN,
        title: 'Merin',
        component: 'MerinPage',
        sections: []
      },
      dravon: {
        path: ROUTES.STRATEGIES.DRAVON,
        title: 'Dravon',
        component: 'DravonPage',
        sections: []
      },
      globalMacro: {
        path: ROUTES.STRATEGIES.GLOBAL_MACRO,
        title: 'Global Macro',
        component: 'GlobalMacroPage',
        sections: []
      },
      amc: {
        path: ROUTES.STRATEGIES.AMC,
        title: 'AMC',
        component: 'AMCPage',
        sections: []
      }
    }
  },

  howToJoin: {
    path: ROUTES.HOW_TO_JOIN.ROOT,
    title: 'How to Join',
    component: 'HowToJoinPage',
    sections: [
      { id: 'careerDevelopment', title: 'Career Development', level: 'h2' },
      { id: 'chooseYourPath', title: 'Choose Your Path', level: 'h2' }
    ],
    subPages: {
      interview: {
        path: ROUTES.HOW_TO_JOIN.INTERVIEW,
        title: 'Interview',
        component: 'InterviewPage',
        sections: [{ id: 'hero', title: 'Interview', level: 'h2' }]
      },
      preparingInterview: {
        path: ROUTES.HOW_TO_JOIN.PREPARING_INTERVIEW,
        title: 'Preparing for Interview',
        component: 'PreparingInterviewPage',
        sections: [
          { id: 'hero', title: 'Preparing for Interview', level: 'h2' }
        ]
      },
      internship: {
        path: ROUTES.HOW_TO_JOIN.INTERNSHIP,
        title: 'Internship',
        component: 'InternshipPage',
        sections: [{ id: 'hero', title: 'Internship', level: 'h2' }]
      },
      ukRecruitment: {
        path: ROUTES.HOW_TO_JOIN.UK_RECRUITMENT,
        title: 'UK Recruitment Policy',
        component: 'UKRecruitmentPage',
        sections: [],
        showInNav: false // Hide from navigation
      },
      euRecruitment: {
        path: ROUTES.HOW_TO_JOIN.EU_RECRUITMENT,
        title: 'EU Recruitment Policy',
        component: 'EURecruitmentPage',
        sections: [],
        showInNav: false // Hide from navigation
      }
    }
  },

  library: {
    path: ROUTES.LIBRARY,
    title: 'Library',
    component: 'LibraryPage',
    sections: [
      { id: 'stayUpdated', title: 'Stay Up to Date', level: 'h2' },
      {
        id: 'factsheetDescription',
        title: 'Strategy Description',
        level: 'h2'
      },
      { id: 'factsheets', title: 'Factsheets', level: 'h2' },
      { id: 'insights', title: 'Insights', level: 'h2' }
    ]
  },

  legal: {
    path: ROUTES.LEGAL.ROOT,
    title: 'Legal',
    component: 'LegalPage',
    sections: [
      {
        id: 'securitization',
        title: 'Securitization in Luxembourg',
        path: fileSecuritizationLuxembourg,
        isFile: true,
        fileType: 'pdf'
      }
    ],
    subPages: {
      importantDisclosures: {
        path: ROUTES.LEGAL.IMPORTANT_DISCLOSURES,
        title: 'Important Disclosures',
        component: 'ImportantDisclosuresPage',
        sections: []
      },
      taxStrategy: {
        path: ROUTES.LEGAL.TAX_STRATEGY,
        title: 'Tax Strategy',
        component: 'TaxStrategyPage',
        sections: []
      },
      modernSlavery: {
        path: ROUTES.LEGAL.MODERN_SLAVERY,
        title: 'Modern Slavery Statement',
        component: 'ModernSlaveryPage',
        sections: []
      },
      terms: {
        path: ROUTES.LEGAL.TERMS,
        title: 'Terms and Conditions of Use',
        component: 'TermsPage',
        sections: []
      },
      recruitmentPolicy: {
        path: ROUTES.LEGAL.RECRUITMENT_POLICY,
        title: 'Recruitment Policy Notice',
        component: 'RecruitmentPolicyPage',
        sections: []
      },
      privacy: {
        path: ROUTES.LEGAL.PRIVACY,
        title: 'Online Privacy Statement',
        component: 'PrivacyPage',
        sections: []
      },
      cookiePolicy: {
        path: ROUTES.LEGAL.COOKIE_POLICY,
        title: 'Cookie Policy',
        component: 'CookiePolicyPage',
        sections: []
      }
    }
  },

  blog: {
    path: ROUTES.BLOG.ROOT,
    title: 'Blog',
    component: 'BlogPage'
  },

  contact: {
    path: ROUTES.CONTACT,
    title: 'Contact',
    component: 'ContactPage'
  },

  portal: {
    path: ROUTES.AUTH,
    title: 'Portal',
    component: 'AuthPage'
  }
};

/**
 * Generate navigation menu from siteMap
 * Creates the menu structure for desktop and mobile navigation
 * Excludes hidden subpages but includes sections and PDF files
 * @returns {Array} Array of navigation menu items
 */
export const generateNavigationMenu = () => {
  return Object.values(siteMap)
    .filter(page => page.title !== 'Portal') // Optionally filter out Portal if needed
    .map(page => {
      const mainItem = {
        title: page.title,
        path: page.path
      };

      // Check if page has sections or subPages
      const hasSections = page.sections && page.sections.length > 0;
      const hasSubPages =
        page.subPages && Object.keys(page.subPages).length > 0;

      // Add dropdown if page has sections or subPages
      if (hasSections || hasSubPages) {
        mainItem.hasDropdown = true;
        mainItem.submenu = [];

        // Add section links
        if (hasSections) {
          page.sections.forEach(section => {
            if (section.isFile) {
              // File link (PDF) - path already contains the imported PDF file
              mainItem.submenu.push({
                title: section.title,
                path: section.path, // Direct PDF path
                isFile: true,
                fileType: section.fileType || 'pdf'
              });
            } else if (section.subItems) {
              // Nested dropdown (not currently used, but supported)
              mainItem.submenu.push({
                title: section.title,
                path: `${page.path}#${section.id}`,
                id: section.id,
                hasSubDropdown: true,
                items: section.subItems.map(subItem => ({
                  title: subItem.title,
                  path: subItem.path,
                  isFile: subItem.isFile || false,
                  fileType: subItem.fileType
                }))
              });
            } else {
              // Regular section link
              mainItem.submenu.push({
                title: section.title,
                path: `${page.path}#${section.id}`,
                id: section.id
              });
            }
          });
        }

        // Add subPage links (only if not hidden)
        if (hasSubPages) {
          Object.values(page.subPages).forEach(subPage => {
            // Only add subPages that are explicitly marked as visible in nav
            // For strategies, subPages are hidden (accessible via cards only)
            if (subPage.showInNav !== false && page.title !== 'Strategies') {
              mainItem.submenu.push({
                title: subPage.title,
                path: subPage.path
              });
            }
          });
        }
      }

      return mainItem;
    });
};

/**
 * Navigation menu structure
 * Auto-generated from siteMap
 */
export const navigationMenu = generateNavigationMenu();

/**
 * Helper: Get all section IDs for a given page path
 * Used for Intersection Observer setup
 * @param {string} pathname - Current page pathname
 * @returns {Array<string>} Array of section IDs for the page
 */
export const getSectionIdsForPath = pathname => {
  const normalizedPath =
    pathname !== '/' && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;

  // Find the page in siteMap
  const page = Object.values(siteMap).find(p => {
    if (p.path === normalizedPath) return true;

    // Check subPages
    if (p.subPages) {
      return Object.values(p.subPages).some(
        subPage => subPage.path === normalizedPath
      );
    }

    return false;
  });

  if (!page) return [];

  // Return section IDs (excluding files)
  return page.sections
    ? page.sections.filter(s => !s.isFile).map(s => s.id)
    : [];
};

/**
 * Helper: Get parent page for a given path
 * Returns the main page object that contains this path
 * @param {string} pathname - Current page pathname
 * @returns {Object|null} Parent page object or null if not found
 */
export const getParentPageForPath = pathname => {
  const normalizedPath =
    pathname !== '/' && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;

  // Direct match
  const directMatch = Object.values(siteMap).find(
    p => p.path === normalizedPath
  );
  if (directMatch) return directMatch;

  // Check if it's a subPage
  for (const page of Object.values(siteMap)) {
    if (page.subPages) {
      const subPageMatch = Object.values(page.subPages).find(
        sp => sp.path === normalizedPath
      );
      if (subPageMatch) return page; // Return parent page
    }
  }

  return null;
};

/**
 * Helper: Check if path is a main route (top-level page)
 * @param {string} pathname - Current page pathname
 * @returns {boolean} True if pathname is a main route
 */
export const isMainRoute = pathname => {
  const normalizedPath =
    pathname !== '/' && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;

  return Object.values(siteMap).some(page => page.path === normalizedPath);
};

const navigationConfig = {
  siteMap,
  navigationMenu,
  getSectionIdsForPath,
  getParentPageForPath,
  isMainRoute
};

export default navigationConfig;
